import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import { useSidebar } from '../../contexts/SidebarContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import attendanceService from '../../services/attendanceService';
import employeeService from '../../services/employeeService';
import NotificationToast from '../../components/ui/NotificationToast';

const PunchPage = () => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const employeeId = searchParams.get('employee_id') || params.employeeId || searchParams.get('id');

  const [employee, setEmployee] = useState(null);
  const [employeesList, setEmployeesList] = useState([]);
  const [loadingEmp, setLoadingEmp] = useState(true);

  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [status, setStatus] = useState('present');
  const [checkInTime, setCheckInTime] = useState('09:00');
  const [checkOutTime, setCheckOutTime] = useState('18:00');
  const [workHours, setWorkHours] = useState('8.0');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    loadEmployees();
  }, [employeeId]);

  const loadEmployees = async () => {
    try {
      setLoadingEmp(true);
      const result = await employeeService.getEmployees({ limit: 500 });
      if (result.success && result.data) {
        setEmployeesList(result.data);
        if (employeeId) {
          const match = result.data.find(e => e.id === employeeId || e.employee_id === employeeId);
          if (match) {
            setEmployee(match);
          } else if (result.data.length > 0) {
            setEmployee(result.data[0]);
          }
        } else if (result.data.length > 0) {
          setEmployee(result.data[0]);
        }
      }
    } catch (err) {
      console.error('Error loading employee list:', err);
    } finally {
      setLoadingEmp(false);
    }
  };

  const statusOptions = [
    { value: 'present', label: 'Present' },
    { value: 'late', label: 'Late Arrival' },
    { value: 'half_day', label: 'Half Day (HD)' },
    { value: 'absent', label: 'Absent' },
    { value: 'leave', label: 'Leave (L)' },
    { value: 'paid_leave', label: 'Paid Leave (PL)' },
    { value: 'week_off', label: 'Week Off (WO)' }
  ];

  const getCurrentTimeString = () => {
    const now = new Date();
    const hrs = String(now.getHours()).padStart(2, '0');
    const mins = String(now.getMinutes()).padStart(2, '0');
    return `${hrs}:${mins}`;
  };

  const handleQuickPunchIn = () => {
    const nowTime = getCurrentTimeString();
    setCheckInTime(nowTime);
    setStatus('present');
    const todayStr = new Date().toISOString().split('T')[0];
    setDate(todayStr);
    setRemarks(`Quick Punch-In at ${nowTime}`);
  };

  const handleQuickPunchOut = () => {
    const nowTime = getCurrentTimeString();
    setCheckOutTime(nowTime);
    const todayStr = new Date().toISOString().split('T')[0];
    setDate(todayStr);
    setRemarks(prev => prev ? `${prev} | Punch-Out at ${nowTime}` : `Quick Punch-Out at ${nowTime}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employee) return;

    try {
      setSaving(true);
      const targetStatus = typeof status === 'object' ? status?.value || status?.target?.value : status;

      const payload = {
        employee_id: employee.id,
        date: date,
        status: targetStatus,
        check_in_time: (targetStatus === 'present' || targetStatus === 'late' || targetStatus === 'half_day') ? checkInTime : null,
        check_out_time: (targetStatus === 'present' || targetStatus === 'late' || targetStatus === 'half_day') ? checkOutTime : null,
        worked_hours: (targetStatus === 'present' || targetStatus === 'late' || targetStatus === 'half_day') ? parseFloat(workHours || 0) : null,
        remarks: remarks || 'Manual Punch Entry'
      };

      const res = await attendanceService.saveAttendanceRecord(payload);
      if (res.success) {
        setNotifications(prev => [{
          id: Date.now(),
          type: 'success',
          title: 'Punch Recorded',
          message: `Successfully recorded punch for ${employee.full_name}`,
          timestamp: new Date()
        }, ...prev]);

        setTimeout(() => {
          navigate('/erp/attendance-management');
        }, 1200);
      } else {
        setNotifications(prev => [{
          id: Date.now(),
          type: 'error',
          title: 'Save Error',
          message: res.error || 'Failed to record attendance punch.',
          timestamp: new Date()
        }, ...prev]);
      }
    } catch (err) {
      setNotifications(prev => [{
        id: Date.now(),
        type: 'error',
        title: 'Error',
        message: err.message || 'Error executing punch action.',
        timestamp: new Date()
      }, ...prev]);
    } finally {
      setSaving(false);
    }
  };

  const employeeOptions = employeesList.map(emp => ({
    value: emp.id,
    label: `${emp.full_name} (${emp.employee_code || emp.employee_id || 'N/A'})`
  }));

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        notifications={notifications}
      />

      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="p-6 max-w-4xl mx-auto min-w-0">
          <BreadcrumbNavigation />

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground">Employee Attendance Punch</h1>
            <p className="text-xs text-muted-foreground">Mark daily punch-in, punch-out, or manual attendance status</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
            {loadingEmp ? (
              <div className="py-12 text-center text-muted-foreground">
                <Icon name="Loader2" size={28} className="animate-spin text-teal-600 mx-auto mb-2" />
                Loading employee details...
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Employee Selection */}
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Select Employee</label>
                  <Select
                    options={employeeOptions}
                    value={employee?.id || ''}
                    onChange={(val) => {
                      const empId = typeof val === 'object' ? val?.target?.value || val?.value : val;
                      const match = employeesList.find(e => e.id === empId);
                      if (match) setEmployee(match);
                    }}
                  />
                </div>

                {/* Quick Punch Buttons */}
                <div className="p-4 bg-muted/30 rounded-xl border border-border flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-bold text-foreground">Quick Action</h4>
                    <p className="text-xs text-muted-foreground">Record live timestamp for {employee?.full_name}</p>
                  </div>
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleQuickPunchIn}
                      className="flex-1 sm:flex-initial bg-emerald-500/10 text-emerald-600 border-emerald-500/20 hover:bg-emerald-500/20 font-semibold"
                      iconName="LogIn"
                      iconPosition="left"
                    >
                      Punch In Now
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleQuickPunchOut}
                      className="flex-1 sm:flex-initial bg-rose-500/10 text-rose-600 border-rose-500/20 hover:bg-rose-500/20 font-semibold"
                      iconName="LogOut"
                      iconPosition="left"
                    >
                      Punch Out Now
                    </Button>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">Attendance Date</label>
                    <Input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">Attendance Status</label>
                    <Select
                      options={statusOptions}
                      value={status}
                      onChange={(val) => setStatus(typeof val === 'object' ? val?.target?.value || val?.value || 'present' : val)}
                    />
                  </div>
                </div>

                {(status === 'present' || status === 'late' || status === 'half_day') && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1.5">Check-In Time</label>
                        <Input
                          type="time"
                          value={checkInTime}
                          onChange={(e) => setCheckInTime(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground mb-1.5">Check-Out Time</label>
                        <Input
                          type="time"
                          value={checkOutTime}
                          onChange={(e) => setCheckOutTime(e.target.value)}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1.5">Worked Hours (DECIMAL)</label>
                      <Input
                        type="number"
                        step="0.25"
                        min="0"
                        max="24"
                        value={workHours}
                        onChange={(e) => setWorkHours(e.target.value)}
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Notes / Remarks</label>
                  <Input
                    type="text"
                    placeholder="Reason or notes..."
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                </div>

                <div className="pt-4 border-t border-border flex items-center justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/erp/attendance-management')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6"
                    iconName={saving ? "Loader2" : "CheckCircle"}
                    iconPosition="left"
                  >
                    {saving ? "Recording Punch..." : "Submit Punch Entry"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>

      <NotificationToast
        notifications={notifications}
        onDismiss={(id) => setNotifications(prev => prev.filter(n => n.id !== id))}
        position="top-right"
      />
    </div>
  );
};

export default PunchPage;
